import { useState, useEffect, useRef, useMemo } from "react";
import { Link as RouterLink, useNavigate, useLocation } from "react-router";
import { Joyride, STATUS } from "react-joyride";
import dayjs from "dayjs";
import {
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import Skeleton from "@mui/material/Skeleton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Add from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import Person from "@mui/icons-material/Person";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ResponsiveTabs from "@nualang/nualang-ui-components/dist/Navigation/ResponsiveTabs";
import TimelineIcon from "@mui/icons-material/Timeline";
import CourseList from "@nualang/nualang-ui-components/dist/Lists/Courses";
import MemberList from "@nualang/nualang-ui-components/dist/Lists/Members";
import MembersDialog from "@nualang/nualang-ui-components/dist/Dialogs/Members";
import InviteDialog from "@nualang/nualang-ui-components/dist/Dialogs/InviteDialog";
import ShareDrawer from "@nualang/nualang-ui-components/dist/Misc/ShareButton/ShareDrawer";
import ClassroomNotFound from "@nualang/nualang-ui-components/dist/Misc/NotFound/Classroom";
import ClassCoursesNotFound from "@nualang/nualang-ui-components/dist/Misc/NotFound/ClassCourses";
import CoursesNotFound from "@nualang/nualang-ui-components/dist/Misc/NotFound/Courses";
import UpdateClassroom from "@nualang/nualang-ui-components/dist/Forms/UpdateClassroom";
import ProgressiveCardMedia from "@nualang/nualang-ui-components/dist/Misc/ProgressiveCardMedia";
import UpgradeSubscription from "@nualang/nualang-ui-components/dist/Dialogs/UpgradeSubscription/UpgradeSubscription";
import CreatorNotSubscribed from "@nualang/nualang-ui-components/dist/Dialogs/CreatorNotSubscribed/CreatorNotSubscribed";
import Overlay from "@nualang/nualang-ui-components/dist/Misc/Overlay";
import DefaultButton from "@nualang/nualang-ui-components/dist/Misc/DefaultColourButton";
import ProgressTable from "@nualang/nualang-ui-components/dist/Tables/Progress";
import ScheduleListCards from "@nualang/nualang-ui-components/dist/Tables/ScheduleListCards/ScheduleListCards";
import { CardAvatar, CardMenuIcon } from "@nualang/nualang-ui-components/dist/Cards/CardElements";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import useAutoRefresh from "../../../hooks/useAutoRefresh.js";
import { trackEvent } from "../../../utils";
import PlaceholderImages from "@nualang/nualang-ui-components/dist/utils/placeholder-images";
import CreateMeetingMultiStepForm from "@nualang/nualang-ui-components/dist/Forms/CreateMeetingMultiStepForm/CreateMeetingMultiStepForm";
import RecordingListCards from "@nualang/nualang-ui-components/dist/Tables/RecordingListCards/RecordingListCards";
import GroupedDiscussions from "@nualang/nualang-ui-components/dist/Tables/MeetingPrompstList/MeetingPromptsList";
import SelectExercise from "@nualang/nualang-ui-components/dist/Dialogs/SelectExercise";
import { Refresh } from "@mui/icons-material";
import useMediaQuery from "@mui/material/useMediaQuery";
import AvatarDialog from "@nualang/nualang-ui-components/dist/Dialogs/AvatarDialog/AvatarDialog";
import { glowPulseLight, glowPulseDark } from "@nualang/nualang-ui-components/dist/utils/Styles";
import enableDiscuss from "@nualang/nualang-ui-components/dist/img/teacher-create-2.svg";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import ArchiveIcon from "@mui/icons-material/Archive";
import ShareIcon from "@mui/icons-material/Share";
import Edit from "@mui/icons-material/Edit";
import Close from "@mui/icons-material/Close";
import ExitToApp from "@mui/icons-material/ExitToApp";
import AssignmentCardsList from "@nualang/nualang-ui-components/dist/Assignments/AssignmentCardsList";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import CreatorNotSubscribedUpgrade from "@nualang/nualang-ui-components/dist/Dialogs/CreatorNotSubscribedUpgrade/CreatorNotSubscribedUpgrade";
import DynamicAIWarning from "@nualang/nualang-ui-components/dist/Dialogs/DynamicAIWarning/DynamicAIWarning";

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
    overflowX: "auto",
  },
  cardTop: {
    position: "relative",
    "@media print": {
      display: "none",
    },
  },
  cardTopAvatar: {
    [theme.breakpoints.down("md")]: {
      width: "60px",
      height: "60px",
    },
    "@media print": {
      display: "none",
    },
    width: "75px",
    height: "75px",
    border: `3px solid ${theme.palette.background.paper}`,
    backgroundColor: theme.palette.mode === "dark" ? "white" : "",
    color:
      theme.palette.mode === "dark" ? theme.palette.background.default : "",
    cursor: "pointer",
    transition: `transform 0.2s ease 0s`,
    "&:hover": {
      transform: `scale(1.08)`,
    },
  },
  header: {
    display: "flex",
    alignItems: "start",
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(2),
  },
  headerText: {
    flexGrow: 1,
  },
  headerTextTitle: {
    fontWeight: 600,
  },
  avatar: {
    height: 40,
    width: 40,
    marginRight: theme.spacing(2),
    "@media print": {
      display: "none",
    },
  },
  coursesTab: {
    position: "relative",
    "@media print": {
      display: "none",
    },
    paddingBottom: "60px",
  },
  moreOptions: {
    "@media print": {
      display: "none",
    },
  },
  printHide: {
    "@media print": {
      display: "none",
    },
  },
  grow: {
    position: "absolute",
    right: 0,
  },
  hideButton: {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  placeholder: {
    height: "100%",
    display: "flex",
    flexGrow: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingTop: theme.spacing(4),
  },
  placeholderContent: {
    textAlign: "center",
    maxWidth: 700,
  },
  placeholderImage: {
    maxWidth: 400,
    marginRight: "40px",
  },
  button: {
    margin: theme.spacing(1),
  },
}));

function OverflowMenu({
  t,
  classroomId,
  anchorEl,
  handleClose,
  isCreator,
  isMember,
  handleOpenFindCourses,
  joinClassroom,
  leaveClassroom,
  deleteClassroom,
  archiveClassroom,
  subscription,
  classroomName,
  trackRecommendedEvent,
  mainSiteUrl,
  members,
  handleOpenInviteDialog,
  refreshClassroomCourses,
  rewatchTutorial,
  courses,
  isArchived,
  isTeacher,
}) {
  const isMediumScreen = useMediaQuery("(max-width:600px)");
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false);
  const [isUpgradeSubscriptionOpen, setIsUpgradeSubscriptionOpen] =
    useState(false);

  const handleOpenUpgradeSubscriptionModal = () => {
    setIsUpgradeSubscriptionOpen(true);
  };

  const handleCloseUpgradeSubcriptionModal = () => {
    setIsUpgradeSubscriptionOpen(false);
  };
  const handleSubscriptionPage = () => {
    window.open(`${mainSiteUrl}/pricing/app`, "_blank");
  };

  const handleClick = () => {
    if (!subscription.isSubscriptionLoaded || subscription.isPaidUser) {
      setIsShareDrawerOpen(true);
    } else {
      handleOpenUpgradeSubscriptionModal();
    }
  };

  return (
    <>
      <Menu
        id={`card-menu-${classroomId}`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {isTeacher && !isArchived && (
          <MenuItem onClick={handleClick} data-cy="share-classroom">
            <CardMenuIcon>
              <ShareIcon fontSize={"small"} />
            </CardMenuIcon>
            <Typography>{t("share")}</Typography>
          </MenuItem>
        )}
        {isCreator && !isArchived && (
          <MenuItem onClick={rewatchTutorial}>
            <CardMenuIcon>
              <LightbulbIcon fontSize={"small"} />
            </CardMenuIcon>
            <Typography>{t("rewatch_tutorial")}</Typography>
          </MenuItem>
        )}
        {!isCreator && !isMember && !isArchived && (
          <MenuItem onClick={joinClassroom}>
            <CardMenuIcon>
              <Add fontSize={"small"} />
            </CardMenuIcon>
            <Typography>{t("join_classroom")}</Typography>
          </MenuItem>
        )}
        {!isCreator && isMember && !isArchived && (
          <MenuItem onClick={leaveClassroom}>
            <CardMenuIcon>
              <ExitToApp fontSize={"small"} />
            </CardMenuIcon>
            <Typography>{t("leave_classroom")}</Typography>
          </MenuItem>
        )}
        {isCreator && courses && courses.length > 0 && (
          <MenuItem onClick={refreshClassroomCourses}>
            <CardMenuIcon>
              <Refresh fontSize={"small"} />
            </CardMenuIcon>
            <Typography>{t("refresh_courses")}</Typography>
          </MenuItem>
        )}
        {isCreator && !isArchived && (
          <MenuItem onClick={handleOpenFindCourses}>
            <CardMenuIcon>
              <Edit fontSize={"small"} />
            </CardMenuIcon>
            <Typography>{t("manage_courses")}</Typography>
          </MenuItem>
        )}
        {isCreator && isArchived && (
          <MenuItem data-cy="delete-classroom" onClick={deleteClassroom}>
            <CardMenuIcon>
              <Close fontSize={"small"} />
            </CardMenuIcon>
            <Typography>{t("delete_classroom")}</Typography>
          </MenuItem>
        )}
        {isCreator && !isArchived && (
          <MenuItem data-cy="archive-classroom" onClick={archiveClassroom}>
            <CardMenuIcon>
              <ArchiveIcon fontSize={"small"} />
            </CardMenuIcon>
            <Typography>{t("archive_classroom")}</Typography>
          </MenuItem>
        )}
        {isMediumScreen && isCreator && !isArchived && (
          <MenuItem
            onClick={
              !subscription.isSubscriptionLoaded || subscription.isPaidUser
                ? handleOpenInviteDialog
                : handleOpenUpgradeSubscriptionModal
            }
          >
            <CardMenuIcon>
              <PersonAddIcon fontSize={"small"} />
            </CardMenuIcon>
            <Typography>{t("invite")}</Typography>
          </MenuItem>
        )}
        {isMediumScreen && isCreator && !(members?.length > 99) && (
          <MenuItem
            component={RouterLink}
            to={`/classrooms/${classroomId}/activity`}
          >
            <CardMenuIcon>
              <TimelineIcon fontSize={"small"} />
            </CardMenuIcon>
            <Typography>{t("view_activity")}</Typography>
          </MenuItem>
        )}
      </Menu>
      <ShareDrawer
        t={t}
        title={classroomName}
        url={window.location.href}
        open={isShareDrawerOpen}
        handleClose={() => setIsShareDrawerOpen(false)}
        subscription={subscription}
        trackRecommendedEvent={trackRecommendedEvent}
        itemId={classroomId}
        contentType="classroom"
      />
      <UpgradeSubscription
        key={"UpgradeSubscription"}
        t={t}
        open={isUpgradeSubscriptionOpen}
        handleClose={handleCloseUpgradeSubcriptionModal}
        onConfirm={handleSubscriptionPage}
        subscription={subscription}
      />
    </>
  );
}

// Shown when the classroom request failed for any reason other than the classroom not existing. The raw reason is shown so support can pass it on.
function ClassroomLoadError({
  t = (text) => text,
  placeholderImageUrl,
  error,
  isRetrying,
  handleRetry,
}) {
  const reason = String(error?.message || error || "").replace(/^Error: /, "");
  return (
    <Box
      data-cy="classroom-load-error"
      sx={{
        height: "100%",
        display: "flex",
        flexGrow: 1,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        {placeholderImageUrl && (
          <Box
            component="img"
            src={placeholderImageUrl}
            alt={t("classroom_load_error")}
            sx={{ maxWidth: 400 }}
          />
        )}
        <Typography component="h1" variant="h5" gutterBottom>
          {t("classroom_load_error")}
        </Typography>
        <Typography component="h2" variant="subtitle1" gutterBottom>
          {t("classroom_load_error_desc")}
        </Typography>
        {reason && (
          <Typography
            data-cy="classroom-load-error-reason"
            variant="caption"
            color="text.secondary"
            component="p"
            gutterBottom
          >
            {reason}
          </Typography>
        )}
        {handleRetry && (
          <Button
            data-cy="classroom-load-error-retry"
            onClick={handleRetry}
            disabled={isRetrying}
            sx={{ m: 1 }}
            variant="contained"
            color="primary"
            endIcon={<Refresh />}
          >
            {t("try_again")}
          </Button>
        )}
      </Box>
    </Box>
  );
}

function ClassroomLoading({ isLoading, t = (text) => text }) {
  const { classes } = useStyles();
  return (
    <>
      <div className={classes.cardTop}>
        <Skeleton
          variant="rectangular"
          sx={(theme) => ({
            [theme.breakpoints.up("md")]: {
              paddingTop: "15%",
            },
            height: 0,
            paddingTop: "25%",
            borderRadius: theme.spacing(2),
            marginBottom: theme.spacing(4),
          })}
        />
      </div>
      <div className={classes.header}>
        <div className={classes.headerText}>
          <Typography variant="h5" component="h1" gutterBottom>
            <Skeleton variant="rectangular" height={32} width={150} />
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <Skeleton variant="rectangular" height={20} width={250} />
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: 2,
              mb: 1
            }}>
            <Box sx={{
              mr: 1
            }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAddIcon />}
                size="small"
                disabled
              >
                {t("join")}
              </Button>
            </Box>
            <DefaultButton
              startIcon={<MoreVertIcon />}
              size="small"
              className={classes.moreOptions}
              disabled
            >
              {t("more_options")}
            </DefaultButton>
          </Box>
        </div>
      </div>
      <ResponsiveTabs
        t={t}
        tabs={[
          { label: t("courses"), id: "Courses" },
          { label: t("assignments"), id: "Assignments" },
          { label: t("progress"), id: "Discuss" },
          { label: t("members"), id: "Members" },
          { label: t("settings"), id: "Settings", disabled: true },
        ]}
        centered
      >
        <Box sx={{
          py: 1
        }}>
          <CourseList t={t} courses={null} />
        </Box>
        <Box sx={{
          py: 1
        }}>
          <AssignmentCardsList
            isLoadingAssignments={true}
            t={t}
            assignments={null}
          />
        </Box>
        <Box sx={{
          py: 1
        }}>
          <ProgressTable isLoading={isLoading} t={t} progress={null} />
        </Box>
        <Box sx={{
          py: 1
        }}>
          <MemberList t={t} members={null} />
        </Box>
      </ResponsiveTabs>
    </>
  );
}

function Classroom({
  t = (text) => text,
  classroom,
  isMember,
  members = [],
  handleJoinClassroom,
  handleLeaveClassroom,
  handleStartCourse,
  handleViewCourse,
  handleViewUserProfile,
  handleLeaveCourse,
  handleViewCourseTopic,
  handleDeleteClassroom,
  handleOpenCreateCourse,
  handleOpenFindCourses,
  membersProps,
  getMemberDetails,
  fileSizeLimit,
  languages,
  forLanguages,
  collaboratorLabels,
  handleUpdateClassroom,
  openSnackbar,
  memberPlaceholderImageUrl,
  coursePlaceholderImageUrl,
  handleRemoveCourse,
  getCourseSections,
  getCourseSectionTopicPhraseLists = () => { },
  getRoleplays,
  getCourseMember,
  getCourseMembers,
  memberId,
  username,
  subscription = false,
  creatorSubscription,
  fetchMemberCourseCompletions,
  mainSiteUrl,
  attributes,
  courses = [],
  isCoursesLoading,
  trackRecommendedEvent,
  authenticated,
  handleSignIn,
  saveAvatarToDynamo,
  handleUpdateAvatar,
  handleUpdateUserLearnLang,
  userInitialValues,
  handleDuplicateCourse,
  freeTrial,
  handleDeleteClassroomMember,
  handleUpdateClassroomMember,
  vchatProps = {},
  handleJoinLiveGame,
  handleCreateLiveGame,
  isSelectExerciseOpen,
  handleOpenSelectExercise,
  handleCloseSelectExercise,
  createdGame,
  isNualangLiveEnabled,
  handleCreateCourse,
  refreshClassroomCourses,
  isTeacher,
  featureFlags,
  assignments,
  handleCreateAssignment,
  deleteAssignment,
  handleEditAssignment,
  getCourses,
  refreshAssignments,
  refreshAssignmentsPoll,
  progressHelpers,
  completionHelpers,
  preferred_username,
  assignedCourses,
  assignmentMembersById,
  email,
  isLoadingAssignments,
  isChallengeModeStudent,
  isReadWriteModeStudent,
  makeChatGptApiRequest,
  siteLanguage,
  handleQuickCreateCourse,
}) {
  // ensure these exist before any use (avoids TDZ / ReferenceError)
  let tabs = [];
  let classroomCreatorWelcome = "classroom_creator_welcome";

  const {
    handleJoinMeeting,
    discussions,
    recordingDiscussions,
    submissionsTableData,
    recordingFeedback,
    formattedScheduleMeeting,
    classroomMembers,
    getRecordings,
    getSchedules,
    playerRef,
    s3Url,
    s3EventsUrl,
    resetVideoState,
    handleBatchGradeConversations,
    isDiscussAIFeedbackEnabled = true,
    handleCreateFeedback,
    aiGrade,
    getMeeting,
    goToTimestamp,
    conversation,
    selectedRecording,
    setSelectedRecording,
    createdDiscussions,
    loading,
    imageLoading,
    saveTranscript,
    handleDeleteDiscussion,
    handleEditDiscussion,
    editBackgroundImages,
    editMeetingImages,
    setSelectedDiscussion,
    meetingRecsData,
    handleArchiveSubmissions,
    isVideoChatEnabled,
    isVideoChatEnabledInSettings,
    startTimes,
    refreshDiscussions,
    refreshRecordings,
  } = vchatProps;

  const isDiscussEnabled = isVideoChatEnabled && isVideoChatEnabledInSettings;

  useAutoRefresh([
    refreshAssignments,
    isDiscussEnabled ? getSchedules : undefined,
    isDiscussEnabled ? refreshDiscussions : undefined,
    isDiscussEnabled ? getRecordings : undefined,
    isDiscussEnabled ? refreshRecordings : undefined,
  ]);

  const isSmallScreen = useMediaQuery("(max-width:410px)");
  const { classes } = useStyles();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isCreateMeetingDialogOpen, setIsCreateMeetingDialogOpen] =
    useState(false);
  const [confirm] = useConfirm(t);
  const [isPictureDialogOpen, setIsPictureDialogOpen] = useState(false);
  const [overwriteValue, setOverwriteValue] = useState(null);
  const [scrollToDiscuss, setScrollToDiscuss] = useState(false);
  const [scrollToDynamicResponses, setScrollToDynamicResponses] =
    useState(false);
  const [displayDynamicAiDialog, setDisplayDynamicAiDialog] = useState(false);
  const [scheduledDynamicAi, setScheduledDynamicAi] = useState(false);
  const isWaysideClassroom = classroom?.classroomId
    ?.toLowerCase()
    .includes("wayside");
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(false);

  const location = useLocation();
  const createLiveGame = location.hash === "#Live";
  const createDiscussion = location.hash === "#Discuss";
  let searchParams;
  let courseSectionTopicId = "";
  if (createLiveGame || createDiscussion) {
    searchParams = new URLSearchParams(location.search);
    courseSectionTopicId = searchParams.get("courseSectionTopicId") || "";
  }
  const removeCourseSectionTopicIdFromUrl = () => {
    const url = new URL(window.location);
    url.searchParams.delete("courseSectionTopicId");
    window.history.replaceState({}, "", url);
  };

  useEffect(() => {
    if (createLiveGame && isNualangLiveEnabled) {
      handleOpenSelectExercise();
    }
  }, [createLiveGame, courseSectionTopicId, isNualangLiveEnabled]);

  useEffect(() => {
    if (createDiscussion && isVideoChatEnabled && courseSectionTopicId) {
      setIsCreateMeetingDialogOpen(true);
    }
  }, [createDiscussion, courseSectionTopicId, isVideoChatEnabled]);

  const joyrideSteps = [
    {
      content: <h2>{t(`${classroomCreatorWelcome}`)}</h2>,
      placement: "center",
      target: "body",
    },
    {
      content: (
        <div>
          <h2>{t("classroom_creator_add_courses")}</h2>
          <br />
          <p>{t("classroom_creator_add_courses_desc")}</p>
        </div>
      ),
      spotlightPadding: 8,
      target: "#add-courses-fab",
    },
    {
      content: (
        <div>
          <h2>{t("classroom_creator_add_students_email")}</h2>
          <br />
          <p>{t("classroom_creator_add_students_email_desc")}</p>
        </div>
      ),
      spotlightPadding: 8,
      target: "#invite",
    },
    {
      content: (
        <div>
          <h2>{t("classroom_creator_play_live_game")}</h2>
          <br />
          <p>{t("classroom_creator_play_live_game_desc")}</p>
        </div>
      ),
      spotlightPadding: 8,
      target: "#play-live",
    },
    {
      content: (
        <div>
          <h2>{t("classroom_creator_track_progress")}</h2>
          <br />
          <p>{t("classroom_creator_track_progress_desc")}</p>
        </div>
      ),
      spotlightPadding: 8,
      target: "#Progress",
    },
  ];

  const [joyrideState, setJoyrideState] = useState({
    run: false,
    steps: joyrideSteps,
  });

  const { run, steps } = joyrideState;

  useEffect(() => {
    const hasDisplayed = localStorage.getItem("displayDynamicAiDialog");
    if (!hasDisplayed && classroom?.isCreator) {
      setScheduledDynamicAi(true);
    }
  }, [classroom]);

  useEffect(() => {
    if (scheduledDynamicAi && !run && !isPictureDialogOpen) {
      setDisplayDynamicAiDialog(true);
      setScheduledDynamicAi(false);
      localStorage.setItem("displayDynamicAiDialog", "true");
    }
  }, [scheduledDynamicAi, run, isPictureDialogOpen]);

  const handleUpdateAvatarAndLearnLang = () => {
    if (!attributes || !attributes.picture) {
      setIsPictureDialogOpen(true);
    }
    if (
      (!attributes || !attributes[`custom:learnLang`]) &&
      classroom &&
      classroom.learnLang
    ) {
      handleUpdateUserLearnLang(classroom.learnLang);
    }
    localStorage.setItem(`classroom-update-avatar-prompt-${username}`, "true");
  };

  useEffect(() => {
    if (
      creatorSubscription?.isSubscriptionLoaded &&
      creatorSubscription?.isPaidUser === false
    ) {
      setIsSubscriptionExpired(true);
    }
  }, [creatorSubscription]);

  useEffect(() => {
    if (
      classroom &&
      classroom.isCreator &&
      creatorSubscription?.isSubscriptionLoaded &&
      creatorSubscription?.isPaidUser === false
    ) {
      handleOpenCreatorNotSubscribedUpgradeModal();
    }
  }, [creatorSubscription]);

  const handleViewProgress = ({ assignmentId }) => {
    setOverwriteValue({
      index: tabs.findIndex((tab) => tab.id === "Progress"),
    });
    navigate(`?assignmentId=${assignmentId}&reportType=assignments#Progress`);
  };

  const tabsRef = useRef(tabs);

  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === "#Settings" && tabsRef.current) {
        setOverwriteValue({
          index: tabsRef.current.findIndex((tab) => tab.id === "Settings"),
        });
      }
    };

    window.addEventListener("hashchange", handleHashChange);

    handleHashChange();

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [
    isNualangLiveEnabled,
    isWaysideClassroom,
    isVideoChatEnabled,
    isVideoChatEnabledInSettings,
  ]);

  const navigate = useNavigate();

  const [hasNewAssignments, setHasNewAssignments] = useState(false);
  const cId = classroom?.classroomId;
  const initialStored = useRef((() => {
    const storedValue = localStorage.getItem("lastAssignmentsFetch");
    try {
      return storedValue ? JSON.parse(storedValue) : {};
    } catch (e) {
      return {};
    }
  })());

  const useSetLastFetched = (condition, assignments, setHasNewAssignments) => {
    useEffect(() => {
      if (!condition || !cId) return;

      const lastFetched = initialStored.current[cId] || 0;
      const hasNew = assignments.some(
        (item) => item.updatedAt && Number(item.updatedAt) > lastFetched,
      );

      setHasNewAssignments(hasNew);

      if (hasNew) {
        const updated = { ...initialStored.current, [cId]: Date.now() };
        localStorage.setItem("lastAssignmentsFetch", JSON.stringify(updated));
      }
    }, [condition, assignments]);
  };

  const condition = !loading && !isCoursesLoading && assignments?.length > 0;
  useSetLastFetched(condition, assignments, setHasNewAssignments);

  classroomCreatorWelcome = freeTrial
    ? "classroom_creator_welcome_free_trial"
    : classroomCreatorWelcome;

  useEffect(() => {
    const isClassroomCreator = classroom && classroom.isCreator;
    const tourCompleted = localStorage.getItem(
      `classroom-creator-tour-completed`,
    );
    const shouldPromptAvatarUpdate = !localStorage.getItem(
      `classroom-update-avatar-prompt-${username}`,
    );

    if (isClassroomCreator && !tourCompleted && !run) {
      setJoyrideState((prevState) => ({ ...prevState, run: true }));
      handleUpdateAvatarAndLearnLang();
    }

    if (
      shouldPromptAvatarUpdate &&
      ((isClassroomCreator && tourCompleted) || isMember)
    ) {
      handleUpdateAvatarAndLearnLang();
    }
  }, [classroom, isMember, username, run]);

  const handleJoyrideCallback = (data) => {
    const { status, action } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status) || action === "close") {
      setJoyrideState((prevState) => ({
        ...prevState,
        run: false,
      }));
      localStorage.setItem(`classroom-creator-tour-completed`, "true");
    }
  };

  const handleClickOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Add courses FAB - a menu instead of opening straight into a dialog,
  // mirroring the section-level Create topic / Find topic split.
  const [addCoursesMenuAnchorEl, setAddCoursesMenuAnchorEl] = useState(null);
  const handleOpenAddCoursesMenu = (event) => {
    setAddCoursesMenuAnchorEl(event.currentTarget);
  };
  const handleCloseAddCoursesMenu = () => setAddCoursesMenuAnchorEl(null);

  const handleRewatchTutorial = () => {
    handleCloseMenu();
    setJoyrideState({ ...joyrideState, run: true });
  };

  const [isMembersOpen, setIsMembersOpen] = useState(false);
  const handleClickOpenMembers = () => {
    setIsMembersOpen(true);
  };
  const handleCloseMembers = () => {
    setIsMembersOpen(false);
  };

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const handleOpenInviteDialog = () => {
    setIsInviteDialogOpen(true);
  };
  const handleCloseInviteDialog = () => {
    setIsInviteDialogOpen(false);
  };

  const [isUpgradeSubscriptionOpen, setIsUpgradeSubscriptionOpen] =
    useState(false);
  const handleOpenUpgradeSubscriptionModal = () => {
    setIsUpgradeSubscriptionOpen(true);
  };
  const handleCloseUpgradeSubcriptionModal = () => {
    setIsUpgradeSubscriptionOpen(false);
  };

  const handleSubscriptionPage = () => {
    window.open(`${mainSiteUrl}/pricing/app`, "_blank");
  };

  const [isCreatorNotSubscribedOpen, setIsCreatorNotSubscribedOpen] =
    useState(false);
  const handleOpenCreatorNotSubscribedModal = () => {
    setIsCreatorNotSubscribedOpen(true);
  };
  const handleCloseCreatorNotSubscribedModal = () => {
    setIsCreatorNotSubscribedOpen(false);
  };

  const [
    isCreatorNotSubscribedUpgradeOpen,
    setIsCreatorNotSubscribedUpgradeOpen,
  ] = useState(false);
  const handleOpenCreatorNotSubscribedUpgradeModal = () => {
    setIsCreatorNotSubscribedUpgradeOpen(true);
  };
  const handleCloseCreatorNotSubscribedUpgradeModal = () => {
    setIsCreatorNotSubscribedUpgradeOpen(false);
  };

  const [avatarOptionClicked, setAvatarOptionClicked] = useState(false);

  const {
    classroomId,
    classroomName,
    description,
    picture,
    pictureXs,
    isCreator,
    createdBy,
    userImage,
    verificationStatus,
    gameAward,
    banner,
    bannerXs,
    visibility,
    enrolmentKey,
    hiddenCourses = [],
  } = classroom;

  const classroomCourses = classroom?.courses || [];
  const courseIds = classroomCourses;
  const assignedCourseIds = classroom?.assignedCourses || [];

  const isArchived = visibility === "archived";

  const handleJoin = () => {
    if (authenticated) {
      if (
        !isArchived &&
        (creatorSubscription.isPaidUser || classroom.visibility === "public")
      ) {
        handleJoinClassroom();
      } else {
        handleOpenCreatorNotSubscribedModal();
      }
    } else {
      handleSignIn();
    }
  };
  const handleOpenUpgradePlans = () => {
    navigate("/settings#Subscription");
  };

  const handleReturnToClassrooms = () => {
    navigate("/classrooms");
  };
  const removeCourse = async (course) => {
    const confirmed = await confirm(
      t("remove_course"),
      t("remove_course_confirmation"),
    );

    if (confirmed) {
      handleRemoveCourse(course);
    }
  };

  const duplicateCourse = async (course) => {
    const confirmed = await confirm(
      t("duplicate_course"),
      t("duplicate_course_confirmation"),
    );
    if (confirmed) {
      handleDuplicateCourse(course);
    }
  };

  const deleteClassroom = async (id) => {
    const confirmed = await confirm(
      t("delete_classroom"),
      t("delete_classroom_confirmation"),
      "",
      true,
      t("classroom_deletion_note", {
        courseCount: courses ? courses.length : 0,
      }),
      t("confirmDeletionText"),
    );

    if (confirmed) {
      handleDeleteClassroom(id);
    }
  };

  const archiveClassroom = async () => {
    const confirmed = await confirm(
      t("archive_classroom"),
      t("archive_classroom_confirmation"),
      "archive",
      false,
      "",
      t("confirmArchiveText"),
      "",
      false,
      true,
    );

    if (confirmed) {
      handleUpdateClassroom({ visibility: "archived" }, {});
    }
  };

  const unarchiveClassroom = async () => {
    const confirmed = await confirm(
      t("unarchive_classroom"),
      t("unarchive_classroom_confirmation"),
      "unarchive",
      false,
      "",
      t("confirmUnarchiveText"),
      "",
      false,
      false,
      true,
    );

    if (confirmed) {
      setAnchorEl(null);
      handleUpdateClassroom(
        { visibility: enrolmentKey ? "private" : "public" },
        {},
      );
    }
  };

  const hideCourse = async (courseId) => {
    handleUpdateClassroom({ hiddenCourses: [...hiddenCourses, courseId] }, {});
  };

  const unhideCourse = async (courseId) => {
    handleUpdateClassroom(
      { hiddenCourses: hiddenCourses.filter((id) => id !== courseId) },
      {},
    );
  };

  const leaveClassroom = async (id) => {
    const confirmed = await confirm(
      t("leave_classroom"),
      t("leave_classroom_confirmation"),
    );

    if (confirmed) {
      handleLeaveClassroom(id);
    }
  };
  const refetchData = async () => {
    if (!isDiscussEnabled) return;
    await getRecordings();
    await getSchedules();
    await refreshRecordings();
    if (refreshDiscussions) await refreshDiscussions();
  };

  const handleGoToDiscussSettings = () => {
    window.location.hash = "#Settings";
    setScrollToDiscuss(true);
  };

  const handleGoToDynamicResponsesSettings = () => {
    setDisplayDynamicAiDialog(false);
    window.location.hash = "#Settings";
    setScrollToDynamicResponses(true);
  };
  tabs = [
    {
      label: t("courses"),
      id: "Courses",
      TabContent: (
        <Box className={classes.coursesTab} key={`tab-content-courses`} sx={{
          py: 1
        }}>
          {isCoursesLoading || (courses !== undefined && courses.length) ? (
            <CourseList
              t={t}
              courses={
                courses !== undefined && courses.length
                  ? isCreator
                    ? courses
                    : courses.filter(
                        (course) => !hiddenCourses.includes(course.courseId),
                      )
                  : null
              }
              handleStartCourse={handleStartCourse}
              handleLeaveCourse={handleLeaveCourse}
              handleViewCourseTopic={handleViewCourseTopic}
              handleViewCourse={handleViewCourse}
              handleViewUserProfile={handleViewUserProfile}
              getMemberDetails={getMemberDetails}
              handleRemoveCourse={isCreator ? removeCourse : null}
              handleHideCourse={isCreator ? hideCourse : null}
              handleUnhideCourse={isCreator ? unhideCourse : null}
              memberPlaceholderImageUrl={memberPlaceholderImageUrl}
              getCourseSections={getCourseSections}
              getCourseMember={getCourseMember}
              getCourseMembers={getCourseMembers}
              memberId={memberId}
              username={username}
              disableActions={!(isMember || isCreator)}
              handleDuplicateCourse={duplicateCourse}
              classroomId={classroomId}
              isClassroomArchived={isArchived}
              hiddenCourses={hiddenCourses}
              email={email}
              isTeacher={isTeacher}
              isClassroomCreator={isCreator}
              showSortControl
            />
          ) : (
            <div id="add-courses-fab">
              {isCreator ? (
                <ClassCoursesNotFound
                  t={t}
                  placeholderImageUrl={coursePlaceholderImageUrl}
                  handleOpenCoursesModal={
                    isCreator ? handleOpenFindCourses : null
                  }
                  handleCreateCourse={isCreator ? handleCreateCourse : null}
                  title={t("classroom_courses_not_found_title")}
                  description={t("classroom_courses_not_found_desc")}
                  handleQuickCreateCourse={handleQuickCreateCourse}
                  learnLang={classroom?.learnLang || attributes?.[`custom:learnLang`]}
                  forLang={classroom?.forLang || attributes?.[`custom:forLang`]}
                  languages={languages}
                  forLanguages={forLanguages}
                  fileSizeLimit={fileSizeLimit}
                  verificationStatus={verificationStatus}
                  userImage={userImage}
                  isCreator={isCreator}
                />
              ) : (
                <CoursesNotFound
                  t={t}
                  placeholderImageUrl={coursePlaceholderImageUrl}
                  handleOpenCoursesModal={
                    isCreator ? handleOpenFindCourses : null
                  }
                  title={t("classroom_courses_not_found_title")}
                  description={t("classroom_courses_not_found_desc")}
                  handleQuickCreateCourse={handleQuickCreateCourse}
                  learnLang={classroom?.learnLang || attributes?.[`custom:learnLang`]}
                  forLang={classroom?.forLang || attributes?.[`custom:forLang`]}
                  languages={languages}
                  forLanguages={forLanguages}
                  fileSizeLimit={fileSizeLimit}
                  verificationStatus={verificationStatus}
                  userImage={userImage}
                  isCreator={isCreator}
                />
              )}
            </div>
          )}
          {!(isMember || isCreator) && !isArchived && (
            <Overlay
              description={t("join_classroom_to_access")}
              buttonText={t("join")}
              onClick={handleJoin}
            />
          )}
          {isMember && isSubscriptionExpired && (
            <Overlay
              description={t("classroom_creator_subscription_expired")}
              buttonText={t("return_to_classrooms")}
              onClick={handleReturnToClassrooms}
            />
          )}
        </Box>
      ),
    },
    {
      label: t("assignments"),
      disabled: isMember && isSubscriptionExpired,
      id: "Assignments",
      TabContent: (
        <Box key={`tab-content-assignments`} sx={{ py: 1 }}>
          <AssignmentCardsList
            t={t}
            assignmentMembersById={assignmentMembersById}
            lastAssignmentFetch={initialStored}
            handleViewProgress={handleViewProgress}
            isCreator={isCreator}
            assignments={assignments}
            courses={courses}
            members={members}
            courseIds={courseIds}
            fetchMemberCourseCompletions={fetchMemberCourseCompletions}
            classroomId={classroomId}
            memberId={!isCreator && username}
            username={username}
            getCourseSections={getCourseSections}
            handleCreateAssignment={handleCreateAssignment}
            deleteAssignment={deleteAssignment}
            handleEditAssignment={handleEditAssignment}
            getRoleplays={getRoleplays}
            getCourses={getCourses}
            refreshAssignments={refreshAssignments}
            progressHelpers={progressHelpers}
            completionHelpers={completionHelpers}
            preferred_username={preferred_username}
            isLoadingAssignments={isLoadingAssignments}
            isChallengeModeStudent={isChallengeModeStudent}
            isReadWriteModeStudent={isReadWriteModeStudent}
            openSnackbar={openSnackbar}
          />
        </Box>
      ),
    },
    ...(isVideoChatEnabled &&
    isNualangLiveEnabled &&
    ((isMember && isVideoChatEnabledInSettings) || isCreator)
      ? [
          {
            label: t("discuss"),
            id: "Discuss",
            disabled: false,
            TabContent: (
              <>
                {isVideoChatEnabled && isVideoChatEnabledInSettings ? (
                  <Box key={`tab-content-video-chat-member`} sx={{
                    py: 1
                  }}>
                    <div className={classes.grow} />
                    {!isCreator && (
                      <Tooltip title={t("refresh_discuss")} placement="left">
                        <IconButton
                          sx={{
                            position: "absolute",
                            right: theme.spacing(),
                            marginRight: theme.spacing(2),
                            zIndex: 1,
                            size: "large",
                            marginTop: "5px",
                          }}
                          size="large"
                          aria-label="Refresh"
                          onClick={refetchData}
                          id="discussRefreshScheduled"
                        >
                          <Refresh fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {
                      <>
                        {isCreator && (
                          <div id="discussGroupedDiscussions">
                            <GroupedDiscussions
                              t={t}
                              userData={classroomMembers}
                              scheduleListData={createdDiscussions}
                              refetchData={refetchData}
                              resetVideoState={resetVideoState}
                              handleDeleteDiscussion={handleDeleteDiscussion}
                              handleEditDiscussion={handleEditDiscussion}
                              editBackgroundImages={editBackgroundImages}
                              editMeetingImages={editMeetingImages}
                              setSelectedDiscussion={setSelectedDiscussion}
                              submissionsTableData={submissionsTableData}
                              handleCreateFeedback={handleCreateFeedback}
                              goToTimestamp={goToTimestamp}
                              conversation={conversation}
                              playerRef={playerRef}
                              s3Url={s3Url}
                              saveTranscript={saveTranscript}
                              s3EventsUrl={s3EventsUrl}
                              handleBatchGradeConversations={
                                handleBatchGradeConversations
                              }
                              aiGrade={aiGrade}
                              isDiscussAIFeedbackEnabled={isDiscussAIFeedbackEnabled}
                              getMeeting={getMeeting}
                              getRecordings={getRecordings}
                              selectedRecording={selectedRecording}
                              setSelectedRecording={setSelectedRecording}
                              meetingRecsData={meetingRecsData}
                              handleArchiveSubmissions={
                                handleArchiveSubmissions
                              }
                              startTimes={startTimes}
                              learnLang={classroom?.learnLang}
                              openSnackbar={openSnackbar}
                              onTrackEvent={(action, label) =>
                                trackEvent("discuss_feedback", action, label)
                              }
                            />
                          </div>
                        )}
                      </>
                    }
                    {!isCreator && (
                      <Box sx={{
                        marginTop: "78px"
                      }}>
                        <div id="discussScheduled">
                          <ScheduleListCards
                            t={t}
                            getRecordings={getRecordings}
                            userData={classroomMembers}
                            joinMeeting={handleJoinMeeting}
                            loading={false}
                            getSchedules={getSchedules}
                            scheduleListData={discussions}
                          />
                        </div>
                      </Box>
                    )}
                    {!isCreator && (
                      <div id="discussRecorded">
                        <RecordingListCards
                          t={t}
                          getRecordings={getRecordings}
                          recordingFeedback={recordingFeedback}
                          recordingsTableData={recordingDiscussions}
                          selectedRecording={selectedRecording}
                          setSelectedRecording={setSelectedRecording}
                          getMeeting={getMeeting}
                          goToTimestamp={goToTimestamp}
                          conversation={conversation}
                          playerRef={playerRef}
                          s3Url={s3Url}
                          resetVideoState={resetVideoState}
                          s3EventsUrl={s3EventsUrl}
                          userData={classroomMembers || []}
                        />
                      </div>
                    )}
                  </Box>
                ) : (
                  isCreator &&
                  isVideoChatEnabled && (
                    <div className={classes.placeholder}>
                      <div className={classes.placeholderContent}>
                        <Typography
                          component="h2"
                          variant="h5"
                          gutterBottom
                          role="alert"
                          sx={{
                            fontWeight: "bold"
                          }}
                        >
                          {t("discuss")}
                        </Typography>
                        <Typography
                          component="h3"
                          variant="subtitle1"
                          gutterBottom
                        >
                          {t("discuss_description")}
                        </Typography>
                        <Box sx={{
                          mb: 1.5
                        }}>
                          {handleGoToDiscussSettings && (
                            <Button
                              onClick={handleGoToDiscussSettings}
                              className={classes.button}
                              variant="contained"
                              color="primary"
                              startIcon={<LockOpenIcon />}
                              role="link"
                              data-cy="enable-discuss"
                            >
                              {t("enable_discuss")}
                            </Button>
                          )}
                        </Box>
                        <img
                          src={enableDiscuss}
                          className={classes.placeholderImage}
                          alt={"Go to settings to enable discuss"}
                        />
                      </div>
                    </div>
                  )
                )}
              </>
            ),
          },
        ]
      : []),
    {
      label: t("progress"),
      disabled: !(isMember || isCreator) || (isMember && isSubscriptionExpired),
      id: "Progress",
      TabContent: (
        <Box key={`tab-content-progress`} sx={{
          pt: 2, pb: 1
        }}>
          <ProgressTable
            t={t}
            assignmentMembersById={assignmentMembersById}
            courses={courses}
            members={members}
            courseIds={courseIds}
            fetchMemberCourseCompletions={fetchMemberCourseCompletions}
            classroomId={classroomId}
            memberId={!isCreator && username}
            username={username}
            getCourseSections={getCourseSections}
            getCourseSectionTopicPhraseLists={getCourseSectionTopicPhraseLists}
            assignments={assignments}
            submissions={submissionsTableData}
            featureFlags={featureFlags}
            isVideoChatEnabled={
              isVideoChatEnabled && isVideoChatEnabledInSettings
            }
            assignedCourses={assignedCourses}
            assignedCourseIds={assignedCourseIds}
          />
        </Box>
      ),
    },
    ...(isCreator
      ? [
          {
            label: `${t("members")} (${members.length < 1000 ? members.length : "999+"})`,
            id: "Members",
            disabled: !(isMember || isCreator),
            TabContent: (
              <Box key={`tab-content-members`} sx={{
                pt: 2, pb: 1
              }}>
                <MemberList
                  t={t}
                  placeholderImageUrl={memberPlaceholderImageUrl}
                  members={
                    Array.isArray(members) && members.length ? members : []
                  }
                  {...membersProps}
                  disableSubheader
                  getMemberDetails={getMemberDetails}
                  isCreator={isCreator}
                  handleDeleteClassroomMember={handleDeleteClassroomMember}
                  handleUpdateClassroomMember={handleUpdateClassroomMember}
                  isClassroomArchived={isArchived}
                  featureFlags={featureFlags}
                />
              </Box>
            ),
          },
        ]
      : []),
    ...(isCreator
      ? [
          {
            label: t("settings"),
            id: "Settings",
            disabled: !isCreator,
            TabContent: (
              <Box key={`tab-content-settings`} sx={{
                pt: 2, pb: 1
              }}>
                <UpdateClassroom
                  t={t}
                  initialValues={classroom}
                  languages={languages}
                  forLanguages={forLanguages}
                  collaboratorLabels={collaboratorLabels}
                  onSubmit={handleUpdateClassroom}
                  fileSizeLimit={fileSizeLimit}
                  subscription={subscription}
                  classroomId={classroomId}
                  courses={courses}
                  scrollToDiscuss={scrollToDiscuss}
                  setScrollToDiscuss={setScrollToDiscuss}
                  scrollToDynamicResponses={scrollToDynamicResponses}
                  setScrollToDynamicResponses={setScrollToDynamicResponses}
                  isClassroomArchived={isArchived}
                  classroomMembers={classroomMembers}
                  verificationStatus={verificationStatus}
                  isVideoChatEnabled={isVideoChatEnabled}
                  openSnackbar={openSnackbar}
                />
              </Box>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    tabsRef.current = tabs;
  });

  const vchatFab = {
    color: "primary",
    loading,
    className: null,
    icon: <Add sx={{ mr: 1 }} />,
    buttonText: t("schedule_discussion"),
    id: "video-chat-fab",
    onClick: () => {
      setIsCreateMeetingDialogOpen(true);
    },
    tabIndex: tabs.findIndex((tab) => tab.id === "Discuss"),
    variant: "extended",
    disabled: !members || members.length === 0,
  };

  const assignmentsFab = {
    color: "primary",
    loading,
    className: null,
    icon: <Add sx={{ mr: 1 }} />,
    buttonText: t("create_assignment"),
    id: "assignments-fab",
    onClick: () => {
      handleCreateAssignment();
    },
    tabIndex: tabs.findIndex((tab) => tab.id === "Assignments"),
    variant: "extended",
    disabled:
      !members || members.length === 0 || !courses || courses.length === 0,
  };

  const fabs = [
    [
      {
        color: "primary",
        className: null,
        icon: <Add sx={{ mr: 1 }} />,
        id: "add-courses-fab",
        buttonText: t("add_courses"),
        onClick: handleOpenAddCoursesMenu,
        tabIndex: tabs.findIndex((tab) => tab.id === "Courses"),
        variant: "extended",
      },
      vchatFab,
      assignmentsFab,
    ],
  ];

  const initialTabValue = useMemo(() => {
    if (window.location.hash) {
      const selectedTabId = window.location.hash.slice(1);
      const index = tabs.findIndex((tab) => tab.id === selectedTabId);
      const tab = tabs[index];
      const isDisabled = tab?.disabled;
      const isRestricted =
        (tab?.id === "Settings" || tab?.id === "Members") && !isCreator;

      if (index >= 0 && !isDisabled && !isRestricted) {
        return index;
      }
    }
    return 0;
  }, [isCreator, tabs?.length]); // only recompute if isCreator or tabs change

  return (
    <>
      <div className={classes.cardTop}>
        <ProgressiveCardMedia
          sx={(theme) => ({
            [theme.breakpoints.up("md")]: {
              paddingTop: "15%",
            },
            height: 0,
            paddingTop: "25%",
            borderRadius: theme.spacing(2),
            marginBottom: theme.spacing(4),
          })}
          highQualitySrc={(banner ?? picture) || PlaceholderImages.classroom}
          lowQualitySrc={bannerXs ?? pictureXs}
          title={classroomName}
        />
        {userImage ? (
          <CardAvatar
            aria-label={`Open ${classroomName} creator's profile`}
            src={userImage}
            className={classes.cardTopAvatar}
            onClick={() => handleViewUserProfile(createdBy)}
            alt=""
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleViewUserProfile(createdBy);
              }
            }}
            role="link"
            tabIndex={0}
            verificationStatus={verificationStatus}
            gameAward={gameAward}
            sx={(theme) => ({
              backgroundColor:
                theme.palette.mode === "dark" ? "#ffffff" : "#bdbdbd",
              color: theme.palette.mode === "dark" ? "#ffffff" : "#bdbdbd",
            })}
          />
        ) : (
          <CardAvatar
            sx={(theme) => ({
              backgroundColor:
                theme.palette.mode === "dark" ? "#ffffff" : "#bdbdbd",
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.background.default
                  : "white",
            })}
            aria-label={`Open ${classroomName} creator's profile`}
            className={classes.cardTopAvatar}
            alt={`${classroomName} creator`}
            onClick={() => handleViewUserProfile(createdBy)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleViewUserProfile(createdBy);
              }
            }}
            role="link"
            tabIndex={0}
            verificationStatus={verificationStatus}
            gameAward={gameAward}
          >
            <Person fontSize="large" />
          </CardAvatar>
        )}
      </div>
      <div className={classes.header}>
        <div className={classes.headerText}>
          <div className={classes.headerTextTitle}>
            <Typography
              variant="h5"
              component="h1"
              data-cy="classroom-title"
              className={classes.headerTextTitle}
            >
              {classroomName}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {description}
            </Typography>
          </div>
          <div className={classes.printHide}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 2,
                mb: 1,
                [theme.breakpoints.down("md")]: { flexWrap: "wrap", gap: 1 }
              }}>
              {isArchived && isCreator && (
                <Box sx={{
                  mr: 1
                }}>
                  <DefaultButton
                    variant="contained"
                    onClick={unarchiveClassroom}
                    startIcon={<UnarchiveIcon />}
                  >
                    {t("unarchive")}
                  </DefaultButton>
                </Box>
              )}
              {isNualangLiveEnabled && isCreator && !isArchived && (
                <Box sx={{
                  mr: 1
                }}>
                  <Tooltip
                    title={
                      courses.length > 0
                        ? t("play_live_desc")
                        : t("empty_live_dialog_description")
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        disabled={courses.length === 0}
                        color="primary"
                        startIcon={<LiveTvIcon />}
                        size="small"
                        id="play-live"
                        data-cy="playLiveButton"
                        sx={{
                          animation:
                            createdGame && createdGame.createdBy === username
                              ? `${theme.palette.mode === "light" ? glowPulseLight : glowPulseDark} 2s infinite ease-in-out`
                              : null,
                          boxShadow:
                            createdGame && createdGame.createdBy === username
                              ? `0 0 0 0 ${theme.palette.mode === "light" ? "rgba(101, 0, 221, 0.7)" : "rgba(171, 120, 255, 0.7)"}`
                              : null,
                        }}
                        onClick={() => {
                          if (createdGame) {
                            if (createdGame.createdBy === username) {
                              navigate(
                                `/classrooms/${classroomId}/games/${createdGame.gameId}`,
                                { state: { isGameCreator: true } },
                              );
                            } else {
                              // Not game creator, joim as a student
                              handleJoinLiveGame(createdGame.gameId);
                            }
                          } else {
                            handleOpenSelectExercise();
                          }
                        }}
                      >
                        {t(
                          createdGame && createdGame.createdBy === username
                            ? "open_live_game"
                            : "",
                        )}
                        {t(
                          createdGame && createdGame.createdBy !== username
                            ? "join_live_game"
                            : "",
                        )}
                        {t(!createdGame ? "play_live" : "")}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              )}
              {isNualangLiveEnabled &&
                !isCreator &&
                isMember &&
                createdGame && (
                  <Box sx={{
                    mr: 1
                  }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<LiveTvIcon />}
                      size="small"
                      onClick={() => {
                        handleJoinLiveGame(createdGame.gameId);
                      }}
                      sx={{
                        animation: `${theme.palette.mode === "light" ? glowPulseLight : glowPulseDark} 2s infinite ease-in-out`,
                        boxShadow: `0 0 0 0 ${theme.palette.mode === "light" ? "rgba(101, 0, 221, 0.7)" : "rgba(171, 120, 255, 0.7)"}`,
                      }}
                    >
                      {t("join_live_game")}
                    </Button>
                  </Box>
                )}
              {!isMember && !isCreator && !isArchived && (
                <Box sx={{
                  mr: 1
                }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PersonAddIcon />}
                    size="small"
                    onClick={handleJoin}
                  >
                    {t("join")}
                  </Button>
                </Box>
              )}
              {isCreator && (!subscription.isSubscriptionLoaded || subscription.isPaidUser) && !isArchived && (
                <Box sx={{
                  mr: 1
                }}>
                  <DefaultButton
                    variant="contained"
                    color="secondary"
                    startIcon={<PersonAddIcon />}
                    size="small"
                    onClick={handleOpenInviteDialog}
                    id="invite"
                    className={classes.hideButton}
                  >
                    {t("invite")}
                  </DefaultButton>
                </Box>
              )}
              {isCreator && subscription.isSubscriptionLoaded && !subscription.isPaidUser && (
                <Box sx={{
                  mr: 1
                }}>
                  <DefaultButton
                    variant="contained"
                    color="secondary"
                    startIcon={<PersonAddIcon />}
                    size="small"
                    onClick={handleOpenUpgradeSubscriptionModal}
                    id="invite"
                    className={classes.hideButton}
                  >
                    {t("invite")}
                  </DefaultButton>
                </Box>
              )}
              {isCreator && !(members?.length > 99) && (
                <Box sx={{
                  mr: 1
                }}>
                  <DefaultButton
                    startIcon={<TimelineIcon />}
                    data-cy="view-activity"
                    size="small"
                    component={RouterLink}
                    to={`/classrooms/${classroomId}/activity`}
                    className={classes.hideButton}
                  >
                    {t("view_activity")}
                  </DefaultButton>
                </Box>
              )}
              <DefaultButton
                startIcon={<MoreVertIcon />}
                size="small"
                onClick={handleClickOpenMenu}
                className={classes.moreOptions}
                data-cy="more-options-button"
                aria-expanded={Boolean(anchorEl)}
                aria-controls={`card-menu-${classroomId}`}
                aria-label={`Level Access Teacher account menu, ${anchorEl ? "expanded" : "collapsed"}`}
              >
                {isSmallScreen ? "" : t("more_options")}
              </DefaultButton>
            </Box>
            <CreatorNotSubscribed
              t={t}
              open={isCreatorNotSubscribedOpen}
              handleClose={handleCloseCreatorNotSubscribedModal}
            />
            <CreatorNotSubscribedUpgrade
              t={t}
              open={isCreatorNotSubscribedUpgradeOpen}
              handleClose={handleCloseCreatorNotSubscribedUpgradeModal}
              submitText={t("upgrade")}
              closeText={t("close")}
              handleSubmit={handleOpenUpgradePlans}
              dialogTitle={t("creator_not_subscribed_upgrade_title")}
              descriptions={[
                "creator_not_subscribed_upgrade_description",
                "creator_not_subscribed_upgrade_description2",
                "creator_not_subscribed_upgrade_description3",
              ]}
            />
          </div>
        </div>
        <div className={classes.printHide}>
          {!run && (
            <AvatarDialog
              t={t}
              dialogTitle={t("update_picture")}
              open={isPictureDialogOpen}
              handleClose={() => setIsPictureDialogOpen(false)}
              name="picture"
              value={
                userInitialValues && userInitialValues.picture
                  ? userInitialValues.picture
                  : ""
              }
              userRole={
                attributes["custom:role"] ? attributes["custom:role"] : "learn"
              }
              handleChange={handleUpdateAvatar}
              saveAvatarToDynamo={saveAvatarToDynamo}
              avatarOptionSelected={avatarOptionClicked}
              setAvatarOptionClicked={setAvatarOptionClicked}
            />
          )}
        </div>
      </div>
      <ResponsiveTabs
        t={t}
        tabs={tabs}
        fabs={
          isCreator &&
          isVideoChatEnabled &&
          isVideoChatEnabledInSettings &&
          !isArchived
            ? courses?.length
              ? fabs
              : [[vchatFab, assignmentsFab]]
            : isCreator && courses?.length && !isArchived
              ? [[fabs[0][0], assignmentsFab]]
              : []
        }
        centered
        scrollable
        hasNewAssignments={hasNewAssignments}
        initialValue={initialTabValue}
        overwriteValue={overwriteValue}
        setOverwriteValue={setOverwriteValue}
        isCreator={isCreator}
        isVideoChatEnabled={isVideoChatEnabled}
        isVideoChatEnabledInSettings={isVideoChatEnabledInSettings}
      >
        {tabs.map((tab) => tab.TabContent)}
      </ResponsiveTabs>
      <OverflowMenu
        t={t}
        classroomId={classroomId}
        anchorEl={anchorEl}
        handleClose={handleCloseMenu}
        handleClickOpenMembers={handleClickOpenMembers}
        isCreator={isCreator}
        isMember={isMember}
        subscription={subscription}
        handleOpenFindCourses={handleOpenFindCourses}
        joinClassroom={handleJoin}
        leaveClassroom={leaveClassroom}
        deleteClassroom={deleteClassroom}
        archiveClassroom={archiveClassroom}
        classroomName={classroomName}
        trackRecommendedEvent={trackRecommendedEvent}
        mainSiteUrl={mainSiteUrl}
        members={members}
        handleOpenInviteDialog={handleOpenInviteDialog}
        refreshClassroomCourses={refreshClassroomCourses}
        rewatchTutorial={handleRewatchTutorial}
        courses={courses}
        isArchived={isArchived}
        isTeacher={isTeacher}
      />
      <Menu
        anchorEl={addCoursesMenuAnchorEl}
        open={Boolean(addCoursesMenuAnchorEl)}
        onClose={handleCloseAddCoursesMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 220, p: 0.5 } }}
      >
        <MenuItem
          onClick={() => {
            handleCloseAddCoursesMenu();
            handleOpenCreateCourse();
          }}
          sx={{
            borderRadius: 1.5,
            mb: 0.5,
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              "& .MuiListItemIcon-root, & .MuiTypography-root": { color: "primary.main" },
            },
          }}
        >
          <CardMenuIcon>
            <AddCircleIcon fontSize="small" />
          </CardMenuIcon>
          <Typography>{t("create_course")}</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseAddCoursesMenu();
            handleOpenFindCourses();
          }}
          sx={{
            borderRadius: 1.5,
            mb: 0.5,
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              "& .MuiListItemIcon-root, & .MuiTypography-root": { color: "primary.main" },
            },
          }}
        >
          <CardMenuIcon>
            <SearchIcon fontSize="small" />
          </CardMenuIcon>
          <Typography>{t("find_course")}</Typography>
        </MenuItem>
      </Menu>
      <UpgradeSubscription
        key={"UpgradeSubscription"}
        t={t}
        open={isUpgradeSubscriptionOpen}
        handleClose={handleCloseUpgradeSubcriptionModal}
        onConfirm={handleSubscriptionPage}
        subscription={subscription}
      />
      <MembersDialog
        t={t}
        placeholderImageUrl={memberPlaceholderImageUrl}
        dialogTitle={classroomName}
        open={isMembersOpen}
        handleClose={handleCloseMembers}
        members={Array.isArray(members) && members.length ? members : []}
        getMemberDetails={getMemberDetails}
        {...membersProps}
      />
      <InviteDialog
        t={t}
        open={isInviteDialogOpen}
        handleClose={handleCloseInviteDialog}
        inviteLink={`${window.location.origin}/classrooms/${classroomId}?plan=learn&learnlang=${classroom.learnLang}`}
        enrolmentKey={
          classroom.visibility === "private" ? classroom.enrolmentKey : ""
        }
        inviteType={"classroom"}
        attributes={attributes}
        trackRecommendedEvent={trackRecommendedEvent}
        itemId={classroomId}
        learnLang={classroom.learnLang}
        isWaysideClassroom={isWaysideClassroom}
      />
      <DynamicAIWarning
        handleSubmit={handleGoToDynamicResponsesSettings}
        open={displayDynamicAiDialog}
        handleClose={() => setDisplayDynamicAiDialog(false)}
        t={t}
      />
      {run && (
        <Joyride
          onEvent={handleJoyrideCallback}
          options={{
            buttons: ['skip', 'primary', 'back', 'close'],
            showProgress: true,
            zIndex: 10000,
              arrowColor: theme.palette.background.paper,
              backgroundColor: theme.palette.background.paper,
              primaryColor: theme.palette.primary.main,
              textColor: theme.palette.text.primary,
          }}
          continuous={true}
          run={run}
          scrollToFirstStep={true}
          steps={steps}
          locale={{
            back: t("back"),
            close: t("close"),
            last: t("last"),
            next: t("next"),
            nextWithProgress: t("next_with_progress"),
            skip: t("skip"),
          }}
        />
      )}
      {isVideoChatEnabled && (
        <CreateMeetingMultiStepForm
          t={t}
          stage={import.meta.env.REACT_APP_STAGE}
          imageLoading={imageLoading}
          handleClose={() => {
            setIsCreateMeetingDialogOpen(false);
            removeCourseSectionTopicIdFromUrl();
          }}
          handleSubmit={async (props) => {
            await formattedScheduleMeeting(props);
            try {
              await refetchData();
            } catch {
              // ignore — dialog must close regardless of refresh failure
            }
            setIsCreateMeetingDialogOpen(false);
            removeCourseSectionTopicIdFromUrl();
          }}
          values={{
            members: classroomMembers,
            recordTimeLimit: 1,
            meetingDate: dayjs().add(1, "day").format("MM/DD/YYYY"),
            meetingTime: dayjs().add(1, "day").format("h:mm A"),
          }}
          isDialog
          loading={loading}
          dialogTitle={t("schedule_discussion")}
          open={isCreateMeetingDialogOpen}
          makeChatGptApiRequest={makeChatGptApiRequest}
          learnLang={classroom.learnLang}
          forLang={classroom.forLang}
          siteLanguage={siteLanguage}
          courses={courses}
          getCourseSections={getCourseSections}
          courseSectionTopicId={courseSectionTopicId}
          createDiscussion={createDiscussion}
          username={username}
        />
      )}
      <SelectExercise
        open={isSelectExerciseOpen}
        courses={courses}
        handleClose={handleCloseSelectExercise}
        getCourseSections={getCourseSections}
        getRoleplays={getRoleplays}
        getPhraseLists={getCourseSectionTopicPhraseLists}
        username={username}
        handleCreateGame={handleCreateLiveGame}
        t={t}
        courseSectionTopicId={courseSectionTopicId}
      />
    </>
  );
}

export default function ViewClassroom({
  t = (text) => text,
  placeholderImageUrl,
  isLoading,
  classroom = {},
  classroomLoadError,
  isRetryingClassroom,
  handleRetryClassroom,
  handleSearchClassrooms,
  featureFlags,
  email,
  coursePlaceholderImageUrl,
  memberPlaceholderImageUrl,
  courses,
  assignedCourses,
  isMember,
  members,
  isCoursesLoading,
  handleLeaveClassroom,
  handleDeleteClassroom,
  handleOpenCreateCourse,
  handleOpenFindCourses,
  refreshClassroomCourses,
  handleJoinClassroom,
  handleDeleteClassroomMember,
  handleUpdateClassroomMember,
  handleStartCourse,
  handleLeaveCourse,
  handleViewCourse,
  getMemberDetails,
  handleViewCourseTopic,
  membersProps,
  languages,
  forLanguages,
  collaboratorLabels,
  handleUpdateClassroom,
  handleRemoveCourse,
  fileSizeLimit,
  getCourseSections,
  getCourseSectionTopicPhraseLists = () => {},
  getRoleplays,
  getCourses,
  memberId,
  getCourseMember,
  getCourseMembers,
  username,
  preferred_username,
  subscription,
  isTeacher,
  creatorSubscription,
  handleViewUserProfile,
  fetchMemberCourseCompletions,
  mainSiteUrl,
  attributes,
  trackRecommendedEvent,
  authenticated,
  handleSignIn,
  handleCreateCourse,
  userInitialValues,
  saveAvatarToDynamo,
  freeTrial,
  handleDuplicateCourse,
  handleUpdateUserLearnLang,
  handleUpdateAvatar,
  vchatProps,
  handleCreateLiveGame,
  handleJoinLiveGame,
  isSelectExerciseOpen,
  handleCloseSelectExercise,
  handleOpenSelectExercise,
  createdGame,
  isNualangLiveEnabled,
  isChallengeModeStudent,
  isReadWriteModeStudent,
  handleCreateAssignment,
  handleEditAssignment,
  assignments,
  refreshAssignments,
  deleteAssignment,
  isLoadingAssignments,
  progressHelpers,
  completionHelpers,
  assignmentMembersById,
  makeChatGptApiRequest,
  siteLanguage,
  handleQuickCreateCourse,
  openSnackbar,
}) {

  const { classes } = useStyles();
  return (
    <div className={classes.root}>
      {isLoading && Object.keys(classroom).length === 0 && (
        <>
          <ClassroomLoading isLoading={isLoading} t={t} />
        </>
      )}
      {classroom && Object.keys(classroom).length > 0 && (
        <>
          <Classroom
            t={t}
            classroom={classroom}
            featureFlags={featureFlags}
            email={email}
            coursePlaceholderImageUrl={coursePlaceholderImageUrl}
            memberPlaceholderImageUrl={memberPlaceholderImageUrl}
            courses={courses}
            assignedCourses={assignedCourses}
            isMember={isMember}
            members={members}
            isCoursesLoading={isCoursesLoading}
            handleLeaveClassroom={handleLeaveClassroom}
            handleDeleteClassroom={handleDeleteClassroom}
            handleOpenCreateCourse={handleOpenCreateCourse}
            handleOpenFindCourses={handleOpenFindCourses}
            refreshClassroomCourses={refreshClassroomCourses}
            handleJoinClassroom={handleJoinClassroom}
            handleDeleteClassroomMember={handleDeleteClassroomMember}
            handleUpdateClassroomMember={handleUpdateClassroomMember}
            handleStartCourse={handleStartCourse}
            handleLeaveCourse={handleLeaveCourse}
            handleViewCourse={handleViewCourse}
            getMemberDetails={getMemberDetails}
            handleViewCourseTopic={handleViewCourseTopic}
            membersProps={membersProps}
            languages={languages}
            forLanguages={forLanguages}
            collaboratorLabels={collaboratorLabels}
            handleUpdateClassroom={handleUpdateClassroom}
            handleRemoveCourse={handleRemoveCourse}
            fileSizeLimit={fileSizeLimit}
            openSnackbar={openSnackbar}
            getCourseSections={getCourseSections}
            getCourseSectionTopicPhraseLists={getCourseSectionTopicPhraseLists}
            getRoleplays={getRoleplays}
            getCourses={getCourses}
            memberId={memberId}
            getCourseMember={getCourseMember}
            getCourseMembers={getCourseMembers}
            username={username}
            preferred_username={preferred_username}
            subscription={subscription}
            isTeacher={isTeacher}
            creatorSubscription={creatorSubscription}
            handleViewUserProfile={handleViewUserProfile}
            fetchMemberCourseCompletions={fetchMemberCourseCompletions}
            mainSiteUrl={mainSiteUrl}
            attributes={attributes}
            trackRecommendedEvent={trackRecommendedEvent}
            authenticated={authenticated}
            handleSignIn={handleSignIn}
            handleCreateCourse={handleCreateCourse}
            userInitialValues={userInitialValues}
            saveAvatarToDynamo={saveAvatarToDynamo}
            freeTrial={freeTrial}
            handleDuplicateCourse={handleDuplicateCourse}
            handleUpdateUserLearnLang={handleUpdateUserLearnLang}
            handleUpdateAvatar={handleUpdateAvatar}
            vchatProps={vchatProps}
            handleCreateLiveGame={handleCreateLiveGame}
            handleJoinLiveGame={handleJoinLiveGame}
            isSelectExerciseOpen={isSelectExerciseOpen}
            handleCloseSelectExercise={handleCloseSelectExercise}
            handleOpenSelectExercise={handleOpenSelectExercise}
            createdGame={createdGame}
            isNualangLiveEnabled={isNualangLiveEnabled}
            isChallengeModeStudent={isChallengeModeStudent}
            isReadWriteModeStudent={isReadWriteModeStudent}
            handleCreateAssignment={handleCreateAssignment}
            handleEditAssignment={handleEditAssignment}
            assignments={assignments}
            refreshAssignments={refreshAssignments}
            deleteAssignment={deleteAssignment}
            isLoadingAssignments={isLoadingAssignments}
            progressHelpers={progressHelpers}
            completionHelpers={completionHelpers}
            assignmentMembersById={assignmentMembersById}
            makeChatGptApiRequest={makeChatGptApiRequest}
            siteLanguage={siteLanguage}
            handleQuickCreateCourse={handleQuickCreateCourse}
          />
        </>
      )}
      {!isLoading &&
        Object.keys(classroom).length === 0 &&
        (classroomLoadError ? (
          <ClassroomLoadError
            t={t}
            placeholderImageUrl={placeholderImageUrl}
            error={classroomLoadError}
            isRetrying={isRetryingClassroom}
            handleRetry={handleRetryClassroom}
          />
        ) : (
          <ClassroomNotFound
            t={t}
            placeholderImageUrl={placeholderImageUrl}
            handleSearchClassrooms={handleSearchClassrooms}
          />
        ))}
    </div>
  );
}
